import debounce from 'lodash/debounce';

import API from '../../helpers/api';
import { NIMBLY_API_REPORT_UPDATE } from '../../../constants/api';
import { isTransientStatus } from '../../../utils/errorClassifier';
import { addBreadcrumb, logErrorWithContext } from '../../../utils/sentry/sentrySetup';
import { track } from '../../../utils/analytics';
import { store } from '../../store';
import {
  setAutoSaveStatus,
  setManualSyncStatus,
  setFormDirty,
  resetSaveStatus
} from '../draftSaveStatus/draftSaveStatus.action';
import * as reportcacheActions from './reportcache.action';
import * as Classes from '../../../utils/classes';

const DEBOUNCE_MS = 1000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 3000;

let activeSaveSession = 0;

function isActiveSaveSession(session: number): boolean {
  return session === activeSaveSession;
}

export interface DraftSaveParams {
  siteKey: string;
  reportKey: string;
  report: Classes.Report;
}

/**
 * Cleans the draft save payload by converting empty arrays to undefined.
 * Matches audit-lite's questionnaire-repository.ts pattern.
 */
function cleanPayload(report: Classes.Report) {
  return {
    questions: report.questions,
    signatures: report.signatures?.length ? report.signatures : undefined,
    selfieSignatures: report.selfieSignatures?.length ? report.selfieSignatures : undefined,
    emailTargets: report.emailTargets?.length ? report.emailTargets : undefined
  };
}

/**
 * Resolves the API report ID from cached report data.
 * Uses apiReportID (set by check-in API) with fallback to reportKey.
 */
function resolveApiReportID(siteKey: string, reportKey: string): string {
  const state = store.getState();
  const cachedReport = state.reportcache.reports?.[siteKey]?.[reportKey];
  return cachedReport?.apiReportID || reportKey;
}

/**
 * Attempts a PUT to the draft save endpoint with retry logic.
 * Max 2 retries, 3s delay, only on transient errors (429, 5xx).
 */
async function putWithRetry(url: string, payload: object): Promise<void> {
  const api = await API.withToken();

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      await api.putJSON(url, payload);
      return;
    } catch (error) {
      const statusMatch = error instanceof Error ? error.message.match(/(\d{3})/) : null;
      const status = statusMatch ? parseInt(statusMatch[1], 10) : 0;
      const isRetryable = status > 0 ? isTransientStatus(status) : true;

      if (isRetryable && attempt < MAX_RETRIES) {
        addBreadcrumb('draftSave', `Retry attempt ${attempt + 1}/${MAX_RETRIES}`, 'warning', {
          url,
          status,
          attempt
        });
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        continue;
      }

      throw error;
    }
  }
}

/**
 * Core save function — writes local cache then calls API.
 * Reads latest state from store at execution time (not stale closures).
 */
async function executeSave(params: DraftSaveParams, isManual: boolean): Promise<void> {
  const { siteKey, reportKey, report } = params;
  const dispatch = store.dispatch;
  const statusAction = isManual ? setManualSyncStatus : setAutoSaveStatus;
  const saveSession = activeSaveSession;

  dispatch(statusAction('saving'));

  addBreadcrumb('draftSave', `${isManual ? 'Manual' : 'Auto'} save started`, 'info', {
    siteKey,
    reportKey
  });

  // Step 1: Local cache write (synchronous)
  try {
    dispatch(reportcacheActions.setCacheReport(siteKey, reportKey, report));
  } catch (error) {
    // Local cache failed — don't attempt API
    dispatch(statusAction('error'));
    logErrorWithContext(error instanceof Error ? error : new Error(String(error)), 'draft_save_local_cache_failed', {
      siteKey,
      reportKey
    });
    return;
  }

  // Step 2: API call (skip if offline)
  const state = store.getState();
  const isConnected = state.network?.isConnected !== false;

  if (!isConnected) {
    addBreadcrumb('draftSave', 'Offline — skipping API call', 'info', { siteKey, reportKey });
    dispatch(statusAction('offline'));
    return;
  }

  try {
    const apiReportID = resolveApiReportID(siteKey, reportKey);
    const url = `${NIMBLY_API_REPORT_UPDATE}/${siteKey}/${apiReportID}/update`;
    const payload = cleanPayload(report);

    await putWithRetry(url, payload);

    if (!isActiveSaveSession(saveSession)) {
      return;
    }

    dispatch(statusAction('saved'));
    // Only manual save clears dirty flag — auto-save is silent background sync
    // (matches audit-lite: form stays "Unsaved" until user explicitly saves)
    if (isManual) {
      dispatch(setFormDirty(false));
    }

    track('draft_saved', { reportId: reportKey, siteId: siteKey, isManual });

    addBreadcrumb('draftSave', `${isManual ? 'Manual' : 'Auto'} save succeeded`, 'info', {
      siteKey,
      reportKey,
      apiReportID
    });
  } catch (error) {
    if (!isActiveSaveSession(saveSession)) {
      return;
    }

    dispatch(statusAction('error'));

    const errMsg = error instanceof Error ? error.message : String(error);
    track('draft_save_failed', { reportId: reportKey, siteId: siteKey, isManual, error: errMsg });

    logErrorWithContext(error instanceof Error ? error : new Error(String(error)), 'draft_save_api_failed', {
      siteKey,
      reportKey,
      isManual
    });

    addBreadcrumb('draftSave', `${isManual ? 'Manual' : 'Auto'} save failed`, 'error', {
      siteKey,
      reportKey,
      error: errMsg
    });
  }
}

// --- Debounced auto-save ---

let pendingParams: DraftSaveParams | null = null;

const debouncedExecute = debounce(async () => {
  if (!pendingParams) return;
  const params = pendingParams;
  pendingParams = null;
  await executeSave(params, false);
}, DEBOUNCE_MS);

/**
 * Schedule a debounced auto-save (2s after last call).
 * Always stores the latest params so the debounced function uses fresh data.
 */
export function save(params: DraftSaveParams): void {
  pendingParams = params;
  store.dispatch(setFormDirty(true));
  // Reset manual sync status so "Saved" (green) hides when user edits again
  store.dispatch(setManualSyncStatus('idle'));
  debouncedExecute();
}

/**
 * Immediately execute a manual save.
 * Cancels any pending auto-save debounce first.
 */
export async function saveNow(params: DraftSaveParams): Promise<void> {
  debouncedExecute.cancel();
  pendingParams = null;
  await executeSave(params, true);
}

/**
 * Flush any pending debounced save immediately (for background/unmount).
 * Best-effort — does not retry on failure.
 */
export function flush(): void {
  debouncedExecute.flush();
}

/**
 * Cancel any pending debounced save without executing it.
 */
export function cancel(): void {
  debouncedExecute.cancel();
  pendingParams = null;
}

/**
 * Reset save status to initial state (call on questionnaire unmount).
 * Invalidates any in-flight async save completion so stale status updates are ignored.
 */
export function reset(): void {
  activeSaveSession += 1;
  cancel();
  store.dispatch(resetSaveStatus());
}

/**
 * Get current pending params (for saveNow to use latest data).
 */
export function getPendingParams(): DraftSaveParams | null {
  return pendingParams;
}

const draftSaveService = {
  save,
  saveNow,
  flush,
  cancel,
  reset,
  getPendingParams
};

export default draftSaveService;
